import type { AccessState } from '@/hooks/queries/useAccountStatus';

export type ExpiryPhase = 'silent' | 'warning' | 'grace' | 'reconversion';

export type WarningTone = 'soft' | 'medium' | 'urgent';

export interface PhaseResult {
  phase: ExpiryPhase;
  /** Sub-tone for the warning phase ("soft" J-7, "medium" J-3, "urgent" J-1). */
  warningTone: WarningTone | null;
  /** Days until something happens — meaning depends on phase. */
  effectiveDays: number;
  /** Human-readable countdown label localized in French. */
  daysLabel: string;
  /** Whether write actions should be locally gated for confidence (server still authoritative). */
  writeRestricted: boolean;
}

const SOFT_THRESHOLD_DAYS = 7;
const MEDIUM_THRESHOLD_DAYS = 3;

function formatDays(days: number): string {
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return '1 jour';
  return `${days} jours`;
}

/**
 * Derive the current expiry phase from server-provided access state.
 *
 * Mapping:
 *   - silent      → no banner / no friction (user is far from expiry)
 *   - warning     → pre-expiry banner (active, ≤7 days left, or trial in soft window)
 *   - grace       → expired but in grace period (still usable, sticky banner + sheet)
 *   - reconversion → hard block (J+14+ or trial fully expired) → reconversion screen
 */
export function derivePhase(
  accessState: AccessState | string | null | undefined,
  daysToExpiry: number,
  graceDaysLeft: number,
  trialDaysLeft: number = 0,
  writeRestricted: boolean = false,
): PhaseResult {
  switch (accessState) {
    case 'blocked':
    case 'trial_expired':
    case 'subscription_suspended':
    case 'account_suspended':
      return {
        phase: 'reconversion',
        warningTone: null,
        effectiveDays: 0,
        daysLabel: '',
        writeRestricted: true,
      };

    case 'grace_soft':
    case 'grace_hard': {
      const days = Math.max(0, graceDaysLeft);
      return {
        phase: 'grace',
        warningTone: null,
        effectiveDays: days,
        daysLabel: formatDays(days),
        writeRestricted: writeRestricted || accessState === 'grace_hard',
      };
    }

    case 'trial_active': {
      if (trialDaysLeft <= 0) {
        return {
          phase: 'reconversion',
          warningTone: null,
          effectiveDays: 0,
          daysLabel: '',
          writeRestricted: true,
        };
      }
      if (trialDaysLeft <= SOFT_THRESHOLD_DAYS) {
        return {
          phase: 'warning',
          warningTone:
            trialDaysLeft <= 1
              ? 'urgent'
              : trialDaysLeft <= MEDIUM_THRESHOLD_DAYS
                ? 'medium'
                : 'soft',
          effectiveDays: trialDaysLeft,
          daysLabel: formatDays(trialDaysLeft),
          writeRestricted: false,
        };
      }
      return {
        phase: 'silent',
        warningTone: null,
        effectiveDays: trialDaysLeft,
        daysLabel: formatDays(trialDaysLeft),
        writeRestricted: false,
      };
    }

    case 'active': {
      if (daysToExpiry <= 0) {
        return {
          phase: 'grace',
          warningTone: null,
          effectiveDays: 0,
          daysLabel: '',
          writeRestricted: false,
        };
      }
      if (daysToExpiry <= SOFT_THRESHOLD_DAYS) {
        return {
          phase: 'warning',
          warningTone:
            daysToExpiry <= 1
              ? 'urgent'
              : daysToExpiry <= MEDIUM_THRESHOLD_DAYS
                ? 'medium'
                : 'soft',
          effectiveDays: daysToExpiry,
          daysLabel: formatDays(daysToExpiry),
          writeRestricted: false,
        };
      }
      return {
        phase: 'silent',
        warningTone: null,
        effectiveDays: daysToExpiry,
        daysLabel: formatDays(daysToExpiry),
        writeRestricted: false,
      };
    }

    default:
      return {
        phase: 'silent',
        warningTone: null,
        effectiveDays: 0,
        daysLabel: '',
        writeRestricted,
      };
  }
}

/**
 * Reconversion percentage progress (0-100) within the 14-day grace window.
 * Used by GracePeriodSheet & banners to show how much of the grace window is consumed.
 */
export function graceProgressPercent(graceDaysLeft: number, hardGraceDays = 14): number {
  if (hardGraceDays <= 0) return 0;
  const consumed = hardGraceDays - Math.max(0, graceDaysLeft);
  return Math.min(100, Math.max(0, (consumed / hardGraceDays) * 100));
}
